import { createContext, useCallback, useContext, useMemo, useState } from 'react'

import type { WorkspaceItem } from '../../../../../../shared/models/WorkspaceItem'
import {
  useCopyWorkspaceItem,
  useMoveWorkspaceItem,
} from '../../../../../../shared/queries/useWorkspaceMediaItems'
import { useWorkspace } from '../../../../../../shared/queries/useWorkspaces'
import { useWorkspaceNavigation } from '../../../../hooks/useWorkspaceNavigation'
import { WorkspaceExplorerMode } from '../enums/WorkspaceExplorerMode'

type WorkspaceExplorerContextValue = {
  workspaceId: string
  workspaceName: string
  currentItemId?: string
  allItems: WorkspaceItem[]
  items: WorkspaceItem[]
  breadcrumbs: { id: string; name: string }[]
  currentParentId: string | null
  selectedItem?: WorkspaceItem
  isLoading: boolean
  isError: boolean
  refetch: () => void
  openFolder: (item: WorkspaceItem) => void
  navigateToRoot: () => void
  navigateToBreadcrumb: (index: number) => void
  openItem: (item: WorkspaceItem) => Promise<void>

  moveItem: (
    itemId: string,
    destinationFolderId: string | null,
    sourceParentId: string | null,
  ) => Promise<void>
  copyItem: (
    itemId: string,
    destinationFolderId: string | null,
    sourceParentId: string | null,
    override?: boolean
  ) => Promise<{ success: boolean, item: WorkspaceItem | undefined }>
}

const WorkspaceExplorerContext = createContext<WorkspaceExplorerContextValue | undefined>(undefined)

export function WorkspaceExplorerProvider({
  workspaceId,
  mode,
  contentMode,
  children,
}: {
  workspaceId: string
  mode: WorkspaceExplorerMode
  contentMode?: 'dialog' | 'content' 
  children: React.ReactNode
}) {
  const { data: workspaceDetail } = useWorkspace(workspaceId)

  const [path, setPath] = useState<string>('')
  const [selectedItem, setSelectedItem] = useState<WorkspaceItem>()

  const updateFolderPath = useCallback((nextPath: string) => {
    setSelectedItem(undefined)
    setPath(nextPath)
  }, [])

  const {
    currentParentId,
    items: allItems,
    breadcrumbs,
    isLoading,
    isError,
    refetch: refetchItems,
    openFolder,
    navigateToRoot,
    navigateToBreadcrumb,
  } = useWorkspaceNavigation(workspaceId, path, updateFolderPath)

  const moveMutation = useMoveWorkspaceItem()
  const copyMutation = useCopyWorkspaceItem()

  const items = useMemo(
    () =>
      mode === WorkspaceExplorerMode.Select ? allItems ?? [] : allItems?.filter((item) => item.type === 'folder') ?? [],
    [allItems, mode],
  )

  const moveItem = useCallback(
    async (itemId: string, destinationFolderId: string | null, sourceParentId: string | null) => {
      try {
        await moveMutation.mutateAsync({
          workspaceId,
          itemId,
          sourceParentId,
          data: { parentId: destinationFolderId },
        })
      } catch (error) {
        console.error('Workspace item move failed', error)
      }
    },
    [moveMutation, workspaceId],
  )

  const copyItem = useCallback(
  async (
    itemId: string,
    destinationFolderId: string | null,
    sourceParentId: string | null,
    override?: boolean
  ): Promise<{ success: boolean, item: WorkspaceItem | undefined }> => {
    try {
      const result = await copyMutation.mutateAsync({
        workspaceId,
        itemId,
        sourceParentId,
        allowDuplicate: override,
        data: { parentId: destinationFolderId },
      })

      return { success: true, item: result.data as WorkspaceItem }
    } catch (error) {
      console.error('Workspace item copy failed', error)

      return { success: false, item: undefined }
    }
  },
  [copyMutation, workspaceId],
)

  const openItem = useCallback(
    (item: WorkspaceItem) => {
      if (item.type === 'folder') {
        openFolder(item)
      } else if (mode === WorkspaceExplorerMode.Select) {
        setSelectedItem(item)
      }

      return Promise.resolve()
    },
    [mode, openFolder],
  )

  const workspaceName = workspaceDetail?.name ?? 'Workspace'

  const value = useMemo(
    () => ({
      workspaceId,
      workspaceName,
      breadcrumbs,
      allItems: allItems ?? [],
      items: items,
      openItem,
      openFolder,
      currentParentId,
      selectedItem,
      isLoading,
      isError,
      refetch: refetchItems,
      navigateToBreadcrumb,
      navigateToRoot,
      moveItem,
      copyItem,
    }),
    [
      workspaceId,
      breadcrumbs,
      workspaceName,
      items,
      allItems,
      openItem,
      openFolder,
      currentParentId,
      selectedItem,
      isLoading,
      isError,
      refetchItems,
      navigateToBreadcrumb,
      navigateToRoot,
      moveItem,
      copyItem,
    ],
  )

  return (
    <WorkspaceExplorerContext.Provider value={value}>{children}</WorkspaceExplorerContext.Provider>
  )
}

export function useWorkspaceExplorer() {
  const context = useContext(WorkspaceExplorerContext)
  if (!context) {
    throw new Error('useWorkspaceExplorer must be used within a WorkspaceExplorerProvider')
  }
  return context
}
